import { apiClient } from "@/services/apiClient";

export interface ProveedorSoftwareSolution {
  id: string;
  codigoProducto: string;
  codigoSolucion: number;
  nombreSw: string;
  modulo: string | null;
  procesos: number[];
  fechaDesde: string;
  fechaHasta: string | null;
}

export interface ProveedorSoftwareEvent {
  id: string;
  type:
    | "cliente_alta"
    | "cliente_baja"
    | "solucion_alta"
    | "solucion_baja"
    | "solucion_modificacion";
  companyId: string | null;
  rucCliente: string | null;
  tipoCliente: number;
  fechaEvento: string;
  periodo: string;
  informeId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ProveedorSoftwareEventsResponse {
  data: ProveedorSoftwareEvent[];
  byPeriodo: Record<string, number>;
}

export type InformeStatus =
  | "pending"
  | "generated"
  | "signed"
  | "submitted"
  | "accepted"
  | "rejected"
  | "failed";

export interface ProveedorSoftwareInforme {
  id: string;
  periodo: string;
  secuencia: number;
  environment: "test" | "prod";
  status: InformeStatus;
  cantSolSoft: number;
  cantRucs: number;
  generatedAt: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  failedAt: string | null;
  createdAt: string;
  /** DGI envío number echoed by the portal acceptance message. */
  dgiEnvioNumero: string | null;
  /** Rejection code from DGI Annex 3 (I01..I09). */
  failureCode: string | null;
  /** Human-readable rejection reason, populated for status='rejected'/'failed'. */
  failureReason: string | null;
}

export interface BuildInformeResponse {
  informeId: string;
  periodo: string;
  secuencia: number;
  environment: "test" | "prod";
  zonaInformada: number;
  cantSolSoft: number;
  cantRucs: number;
}

export interface SignInformeResponse {
  informeId: string;
  issuerName: string;
  serialNumber: string;
}

export interface BaselineIngestResponse {
  inserted: number;
  skipped: number;
  errors: { rut: string; message: string }[];
}

export interface ProveedorSoftwareAutomationConfig {
  enabled: boolean;
  cadence: "manual" | "monthly";
  dayOfMonth: number;
  environment: "test" | "prod";
  autoSubmit: boolean;
  lastRunAt: string | null;
  lastRunResult: Record<string, unknown> | null;
}

export type ProveedorSoftwareAutomationConfigUpdate = Partial<
  Pick<
    ProveedorSoftwareAutomationConfig,
    "enabled" | "cadence" | "dayOfMonth" | "environment" | "autoSubmit"
  >
>;

const BASE = "/platform/proveedor-software";

export function getSolucion(): Promise<ProveedorSoftwareSolution> {
  return apiClient.get<ProveedorSoftwareSolution>(`${BASE}/solucion`).then((r) => r.data);
}

export function updateSolucionFechaDesde(fechaDesde: string): Promise<void> {
  return apiClient
    .patch(`${BASE}/solucion/fecha-desde`, { fechaDesde })
    .then(() => undefined);
}

export function listEvents(periodo?: string): Promise<ProveedorSoftwareEventsResponse> {
  return apiClient
    .get<ProveedorSoftwareEventsResponse>(`${BASE}/events`, {
      params: periodo ? { periodo } : undefined,
    })
    .then((r) => r.data);
}

export function listInformes(): Promise<ProveedorSoftwareInforme[]> {
  return apiClient.get<ProveedorSoftwareInforme[]>(`${BASE}/informes`).then((r) => r.data);
}

export function buildInforme(
  periodo: string,
  environment: "test" | "prod" = "test",
): Promise<BuildInformeResponse> {
  return apiClient
    .post<BuildInformeResponse>(`${BASE}/informes/build`, { periodo, environment })
    .then((r) => r.data);
}

export function signInforme(informeId: string): Promise<SignInformeResponse> {
  return apiClient
    .post<SignInformeResponse>(`${BASE}/informes/${informeId}/sign`)
    .then((r) => r.data);
}

export interface MarkInformeResultParams {
  outcome: "accepted" | "rejected";
  envioNumero: string;
  motivoCode?: string;
  motivoMensaje?: string;
}

export interface MarkInformeResultResponse {
  id: string;
  status: InformeStatus;
  dgiEnvioNumero: string | null;
  failureCode: string | null;
  failureReason: string | null;
}

/**
 * Record the manual-upload outcome of an informe on the DGI portal.
 * Used as the fallback when the Python auto-upload flow fails to
 * classify (or isn't available).
 */
export function markInformeResult(
  informeId: string,
  params: MarkInformeResultParams,
): Promise<MarkInformeResultResponse> {
  return apiClient
    .post<MarkInformeResultResponse>(
      `${BASE}/informes/${informeId}/mark-result`,
      params,
    )
    .then((r) => r.data);
}

export interface UploadInformeToDgiResponse {
  accepted: boolean;
  sessionId: string;
  message?: string;
}

/**
 * Trigger the eFactura portal auto-upload via the Python automation
 * flow. Returns 202 once the Python service has accepted the start
 * request — the actual DGI outcome lands later on the webhook and
 * flips the informe row to accepted/rejected.
 */
export function uploadInformeToDgi(
  informeId: string,
): Promise<UploadInformeToDgiResponse> {
  return apiClient
    .post<UploadInformeToDgiResponse>(
      `${BASE}/informes/${informeId}/upload-to-dgi`,
    )
    .then((r) => r.data);
}

/**
 * Download the informe XML via apiClient so the axios interceptors
 * attach the bearer token — a raw <a href> would let the browser
 * make a naked GET that bypasses our auth header (the JS-memory
 * bearer never reaches the server) and the AuthGuard rejects with
 * 401. Falls back to a deterministic filename when the response
 * doesn't carry a Content-Disposition.
 */
export async function downloadInformeXml(
  informeId: string,
  signed: boolean,
): Promise<void> {
  const qs = signed ? "?signed=1" : "";
  const res = await apiClient.get<Blob>(
    `${BASE}/informes/${informeId}/xml${qs}`,
    { responseType: "blob" },
  );
  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data as BlobPart], { type: "application/xml" });
  const filename =
    parseContentDispositionFilename(res.headers) ??
    `Prov_${informeId.slice(0, 8)}${signed ? "_signed" : ""}.xml`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function parseContentDispositionFilename(
  headers: Record<string, unknown>,
): string | null {
  const raw =
    (headers["content-disposition"] as string | undefined) ??
    (headers["Content-Disposition"] as string | undefined);
  if (!raw) return null;
  const star = raw.match(/filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/\+/g, " "));
    } catch {
      // fall through to plain filename=
    }
  }
  const quoted = raw.match(/filename="([^"]+)"/);
  if (quoted?.[1]) return quoted[1];
  const unquoted = raw.match(/filename=([^;\s]+)/);
  if (unquoted?.[1]) return unquoted[1].replace(/^["']|["']$/g, "");
  return null;
}

export function ingestBaseline(file: File): Promise<BaselineIngestResponse> {
  const fd = new FormData();
  fd.append("file", file);
  return apiClient
    .post<BaselineIngestResponse>(`${BASE}/baseline/ingest`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
}

export function getAutomationConfig(): Promise<ProveedorSoftwareAutomationConfig> {
  return apiClient
    .get<ProveedorSoftwareAutomationConfig>(`${BASE}/automation`)
    .then((r) => r.data);
}

export function updateAutomationConfig(
  patch: ProveedorSoftwareAutomationConfigUpdate,
): Promise<ProveedorSoftwareAutomationConfig> {
  return apiClient
    .patch<ProveedorSoftwareAutomationConfig>(`${BASE}/automation`, patch)
    .then((r) => r.data);
}
